import { t } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Textarea,
  addToast,
} from "@heroui/react";
import { useForm } from "@mantine/form";
import { useEffect, useRef, useState } from "react";
import { count } from "sms-length";
import { apiPostBulkSms } from "./CompletedApi";

interface BulkSMSModalProps {
  selectedCustomerIds: number[];
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
}

export const BulkSMSModal = ({
  selectedCustomerIds,
  isOpen,
  onOpenChange,
}: BulkSMSModalProps) => {
  const messageRef = useRef<HTMLTextAreaElement>(null);
  const [isSendingSms, setIsSendingSms] = useState(false);

  const tags = [
    { label: t`Ime stranke`, value: "{customerName}", length: 10 },
    { label: t`Priimek stranke`, value: "{customerLastName}", length: 10 },
    {
      label: t`Povezava za prenaročanje`,
      value: "{rescheduleLink}",
      length: 23,
    },
  ];

  const form = useForm({
    initialValues: {
      message: "",
    },
  });

  useEffect(() => {
    if (isOpen) form.reset();
  }, [isOpen]);

  const handleTagClick = (tagValue: string) => {
    const cursorPos = messageRef.current?.selectionStart ?? 0;

    form.setFieldValue(
      "message",
      form.values.message.slice(0, cursorPos) +
        tagValue +
        form.values.message.slice(cursorPos),
    );
  };

  const insertStringsOfTagsLengthInContent = (content: string | undefined) => {
    if (!content) return;
    let newContent = content;
    tags.forEach((tag) => {
      newContent = newContent.replaceAll(tag.value, "X".repeat(tag.length));
    });
    return newContent;
  };

  const contentWithTags = insertStringsOfTagsLengthInContent(
    form.values.message,
  );
  const smsInfo = count(replaceSumniki(contentWithTags ?? "") ?? "");

  const handleSendBulkSms = async () => {
    setIsSendingSms(true);
    try {
      await sendBulkSms({
        selectedCustomerIds,
        message: form.values.message,
      });
      onOpenChange(false);
    } catch {
      addToast({
        title: t`Napaka`,
        description: t`Sporočila niso bila poslana`,
        color: "danger",
      });
    }
    setIsSendingSms(false);
  };

  return (
    <Modal
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      size="md"
      placement="center"
    >
      <ModalContent>
        <ModalHeader className="flex flex-col gap-1">
          <h2 className="text-lg font-semibold">
            <Trans>Pošlji SMS {selectedCustomerIds.length}. strankam.</Trans>
          </h2>
        </ModalHeader>
        <ModalBody>
          <div className="flex flex-col gap-4">
            <span>
              <Trans>Vnesite sporočilo:</Trans>
            </span>
            <div className="flex flex-wrap gap-2">
              {tags.map((tag, idx) => (
                <button
                  key={idx}
                  className="rounded-full bg-gray-100 px-3 py-1 text-sm text-gray-600 transition-colors hover:bg-green-50 hover:text-green-600"
                  onClick={() => handleTagClick(tag.value)}
                >
                  {tag.label}
                </button>
              ))}
            </div>
            <Textarea
              ref={messageRef}
              {...form.getInputProps("message")}
              required
              label={t`Sporočilo`}
              minRows={4}
              autoFocus
              className="w-full"
            />
            <p className="mt-2 text-sm text-gray-600">
              <Trans>Porabljenih znakov</Trans>:{" "}
              {smsInfo.characterPerMessage - smsInfo.remaining} od{" "}
              {smsInfo.characterPerMessage} <Trans>v</Trans> {smsInfo.messages}{" "}
              <Trans>SMS sporočilih</Trans>
            </p>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button
            color="primary"
            isLoading={isSendingSms}
            onPress={handleSendBulkSms}
          >
            <Trans>Pošlji</Trans>
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

const sendBulkSms = async ({
  selectedCustomerIds,
  message,
}: {
  selectedCustomerIds: number[];
  message: string;
}) => {
  addToast({
    title: t`Uspešno`,
    description: t`Sporočila bodo poslana`,
    color: "success",
  });

  return await apiPostBulkSms(message, selectedCustomerIds);
};

const replaceSumniki = (string: string) => {
  return string
    .replaceAll("č", "c")
    .replaceAll("š", "s")
    .replaceAll("ž", "z")
    .replaceAll("Č", "C")
    .replaceAll("Š", "S")
    .replaceAll("Ž", "Z")
    .replaceAll("ć", "c")
    .replaceAll("đ", "dj")
    .replaceAll("Ć", "C")
    .replaceAll("Đ", "Dj");
};
